import { useCallback, useEffect, useState } from 'react';

export const PANE_WIDTHS = ['narrow', 'medium', 'wide'];
export const DEFAULT_PANE_WIDTH = 'medium';

// Wide needs the items page body (the list area beside the rail, not the
// window: owner, 2026-10-30) at least this wide, so the list keeps its 560px
// beside a pane of 640px or half the body.
export const WIDE_MIN_BODY = 1280;
export const WIDE_DISABLED_REASON = 'Wide needs a wider window.';

export const paneWidthKey = collectionId => `keepr.items.pane.${collectionId || ''}`;

/** The stored width for a collection, or the default when none is stored (or storage is unavailable). */
export function readPaneWidth(collectionId) {
	try {
		const stored = window.localStorage.getItem(paneWidthKey(collectionId));
		return PANE_WIDTHS.includes(stored) ? stored : DEFAULT_PANE_WIDTH;
	} catch {
		return DEFAULT_PANE_WIDTH;
	}
}

/**
 * THE DETAIL PANE'S WIDTH on the items page (KPR-1643): narrow, medium or
 * wide, one choice per collection, remembered in this browser the way row
 * density is (useRowDensity). A per-viewer convenience, so a storage failure
 * only means the choice is not remembered.
 *
 * `bodyRef` goes on the items page body (a callback ref, so the measure
 * starts whenever that element mounts); while the body is under
 * WIDE_MIN_BODY Wide is not allowed and a remembered Wide is drawn as Medium.
 * The stored choice is kept, so a wider window brings it back.
 *
 *   const { width, chosen, setWidth, step, wideAllowed, bodyRef } = usePaneWidth(collectionId);
 */
export default function usePaneWidth(collectionId) {
	const [chosen, setChosen] = useState(() => readPaneWidth(collectionId));
	const [wideAllowed, setWideAllowed] = useState(true);

	// Another collection has its own choice.
	useEffect(() => {
		setChosen(readPaneWidth(collectionId));
	}, [collectionId]);

	const [body, bodyRef] = useState(null);
	useEffect(() => {
		if (!body) return undefined;
		const measure = () => setWideAllowed(body.getBoundingClientRect().width >= WIDE_MIN_BODY);
		measure();
		if (typeof ResizeObserver !== 'function') return undefined;
		const observer = new ResizeObserver(measure);
		observer.observe(body);
		return () => observer.disconnect();
	}, [body]);

	const width = chosen === 'wide' && !wideAllowed ? 'medium' : chosen;

	const setWidth = useCallback(next => {
		if (!PANE_WIDTHS.includes(next)) return;
		setChosen(next);
		try {
			window.localStorage.setItem(paneWidthKey(collectionId), next);
		} catch { /* per-viewer convenience only */ }
	}, [collectionId]);

	// `[` and `]`: one width narrower or wider, from the width drawn, never
	// past the ends nor onto a Wide that is not allowed.
	const step = useCallback(delta => {
		const index = PANE_WIDTHS.indexOf(width) + (delta > 0 ? 1 : -1);
		const next = PANE_WIDTHS[index];
		if (!next || (next === 'wide' && !wideAllowed)) return;
		setWidth(next);
	}, [width, wideAllowed, setWidth]);

	return { width, chosen, setWidth, step, wideAllowed, bodyRef };
}
